import { type ChangeEvent, forwardRef, useRef } from "react";
import TextField, { type OutlinedTextFieldProps, type TextFieldProps } from "@mui/material/TextField";
import { InputAdornment } from "@mui/material";
import IconComponent from "@CubeComponents/icons";

type CubeTextFieldProps = Omit<OutlinedTextFieldProps, "variant"> & {
  variant?: "outlined" | "filled" | "standard";
  showClearButton?: boolean;
  height?: string;
  width?: string;
  /** @deprecated MUI v9 removed this top-level prop — shimmed to `slotProps.input`. */
  InputProps?: Record<string, unknown>;
  /** @deprecated MUI v9 removed this top-level prop — shimmed to `slotProps.htmlInput`. */
  inputProps?: Record<string, unknown>;
  /** @deprecated MUI v9 removed this top-level prop — shimmed to `slotProps.inputLabel`. */
  InputLabelProps?: Record<string, unknown>;
  /**
   * Syncfusion-era spelling of `disabled`. Destructured out below (so it never reaches the DOM input)
   * and folded into MUI's own `disabled`, which an explicit `disabled` still wins over.
   */
  isDisabled?: boolean;
  // KNOWN BUG, preserved verbatim: same story as `isDisabled` - `isError` is a Syncfusion
  // prop name that MUI ignores and spreads onto the DOM input.
  isError?: boolean;
};

/**
 * The plain-object branch of MUI's `SxProps<Theme>` union. `sx` is declared as that union (object |
 * callback | array), so the two nested selector reads below cannot index it directly. Every caller
 * of this component passes the object form. Leaves are only ever spread, never read as primitives,
 * which is why they can be typed as nested selector objects.
 */
type SxSelectorObject = { [selector: string]: SxSelectorObject | undefined };

/**
 * True for the plain-object branch of `SxProps<Theme>`. The callback and array branches carry no
 * selector keys, so they read as "no overrides" — which is exactly what indexing them produced
 * before. Declared over `unknown` because the object branch is not assignable to the recursive
 * index signature above, so no annotation on `props.sx` could express the same narrowing.
 */
function isSxSelectorObject(value: unknown): value is SxSelectorObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A slot entry as a plain bag of props. MUI declares each slot as `Partial<Slot> | ((ownerState) =>
 * Partial<Slot>)`; the shim below only ever merges into a slot and reads `endAdornment` back, so
 * the callback form reads as "nothing to merge" — which is what indexing it produced before.
 */
function asSlotObject(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

const TextFieldComponent = forwardRef<HTMLInputElement, CubeTextFieldProps>((props, ref) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const {
    height = "32px",
    sx = {},
    multiline = false,
    showClearButton,
    isDisabled,
    InputProps: legacyInputProps,
    inputProps: legacyHtmlInputProps,
    InputLabelProps: legacyInputLabelProps,
    ...restProps
  } = props;

  // The caller's own nested-selector overrides, merged into the two rules below. `sx` is optional,
  // so this stays `undefined` when nothing was passed and the reads below remain optional-chained.
  const sxOverrides = isSxSelectorObject(props.sx) ? props.sx : undefined;

  const setRefs = (element: HTMLInputElement | null) => {
    internalRef.current = element;
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
    }
  };

  // MUI v9 removed the top-level InputProps / inputProps / InputLabelProps in favour of
  // `slotProps`. Fold any legacy props callers still pass into the v9 slot equivalents
  // (caller-supplied `slotProps` wins), then layer the clear-button adornment on top.
  const slotProps: Record<string, unknown> = { ...restProps.slotProps };
  if (legacyInputProps) slotProps.input = { ...legacyInputProps, ...asSlotObject(slotProps.input) };
  if (legacyHtmlInputProps) slotProps.htmlInput = { ...legacyHtmlInputProps, ...asSlotObject(slotProps.htmlInput) };
  if (legacyInputLabelProps) slotProps.inputLabel = { ...legacyInputLabelProps, ...asSlotObject(slotProps.inputLabel) };
  if (showClearButton && !asSlotObject(slotProps.input).endAdornment) {
    slotProps.input = {
      ...asSlotObject(slotProps.input),
      endAdornment: (
        <InputAdornment position="end">
          <IconComponent
            name="cross"
            onClick={() => {
              if (internalRef.current) {
                internalRef.current.value = "";
                // React's SyntheticEvent cannot be constructed outside React's own event system,
                // so the clear button hands `onChange` the real input element as `target` right
                // after blanking its value — which is what `e.target.value` / `e.target.name`
                // consumers read.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above; a real ChangeEvent is not constructible here
                restProps.onChange?.({ target: internalRef.current } as ChangeEvent<HTMLInputElement>);
              }
            }}
            width="1.125rem"
            height="1.125rem"
            className="cursor-pointer"
          />
        </InputAdornment>
      ),
    };
  }

  return (
    <TextField
      inputRef={setRefs}
      multiline={multiline}
      sx={{
        width: props.width ?? "100%",
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        ...sx,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "var(--surface-white)",
          ...sxOverrides?.["& .MuiOutlinedInput-root"],
          minHeight: height,
          borderColor: "#666A84",
          paddingTop: "0 !important",
          paddingInlineStart: "0 !important",
          paddingBottom: "0 !important",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#666A84",
          opacity: 1,
        },

        "& .MuiOutlinedInput-root:hover .MuiInputBase-input::placeholder": {
          color: "#192048 !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#666A84",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#6BBBE3",
          borderWidth: "1px",
          ...sxOverrides?.["& .MuiOutlinedInput-root"]?.["&.Mui-focused fieldset"],
        },
        "& .MuiOutlinedInput-root.Mui-disabled": {
          backgroundColor: "var(--light-blue-tag)",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
          backgroundColor: "var(--surface-white)",
          padding: "0.3125rem 0.625rem !important",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input": {
          backgroundColor: "inherit",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
      }}
      {...restProps}
      disabled={restProps.disabled ?? isDisabled}
      // The bag above is assembled from three deprecated top-level props plus whatever the caller
      // already passed in `slotProps`, so it is only knowable as a record of slot names. MUI's own
      // per-slot types are re-applied here, at the single point where it reaches the component.
      slotProps={slotProps as TextFieldProps["slotProps"]}
    />
  );
});

TextFieldComponent.displayName = "TextFieldComponent";

export default TextFieldComponent;
